import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { InviteCreatorModal } from '../components/InviteCreatorModal';
import { 
    ArrowLeft, 
    Calendar,
    Search,
    Play,
    Pause,
    Loader2,
    GripVertical,
    User,
    Music,
    Clock,
    DollarSign,
    ExternalLink,
    XCircle,
    CheckCircle,
    Send,
    Pencil
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const KANBAN_COLUMNS = [
    { id: 'new', title: 'New', color: 'bg-blue-500' },
    { id: 'reviewing', title: 'Reviewing', color: 'bg-amber-500' },
    { id: 'accepted', title: 'Accepted', color: 'bg-green-500' },
    { id: 'rejected', title: 'Rejected', color: 'bg-red-500' }
];

export default function PosterProjectDetailPage() {
    const { projectId } = useParams();
    const { token, isAdmin } = useAuth();
    const [project, setProject] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const [playingAudio, setPlayingAudio] = useState(null);
    const [audioElement, setAudioElement] = useState(null);
    const [statusDialog, setStatusDialog] = useState({ open: false, newStatus: null });
    const [updatingStatus, setUpdatingStatus] = useState(false);
    
    // Deadline edit state
    const [showDeadlineModal, setShowDeadlineModal] = useState(false);
    const [selectedDeadline, setSelectedDeadline] = useState(null);
    const [updatingDeadline, setUpdatingDeadline] = useState(false);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchProject();
        fetchSubmissions();
    }, [projectId]);

    useEffect(() => {
        // Cleanup audio on unmount
        return () => {
            if (audioElement) {
                audioElement.pause();
            }
        };
    }, [audioElement]);

    const fetchProject = async () => {
        try {
            const response = await fetch(`${API}/poster/projects/${projectId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setProject(data);
            }
        } catch (error) {
            toast.error('Error loading project');
        }
    };

    const fetchSubmissions = async () => {
        try {
            const response = await fetch(`${API}/poster/projects/${projectId}/submissions`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSubmissions(data);
            }
        } catch (error) {
            toast.error('Error loading submissions');
        } finally {
            setLoading(false);
        }
    };

    const toggleProjectStatus = async () => {
        if (!statusDialog.newStatus) return;
        
        setUpdatingStatus(true);
        try {
            const response = await fetch(`${API}/poster/projects/${projectId}`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: statusDialog.newStatus })
            });
            
            if (response.ok) {
                setProject(prev => ({ ...prev, status: statusDialog.newStatus }));
                toast.success(statusDialog.newStatus === 'closed' 
                    ? 'Project closed - artists can no longer submit' 
                    : 'Project reopened - artists can now submit'
                );
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to update project status');
            }
        } catch (error) {
            toast.error('Error updating project status');
        } finally {
            setUpdatingStatus(false);
            setStatusDialog({ open: false, newStatus: null });
        }
    };
    
    const handleUpdateDeadline = async () => {
        if (!selectedDeadline) return;
        
        setUpdatingDeadline(true);
        try {
            // Format date as YYYY-MM-DD
            const formattedDeadline = selectedDeadline.toISOString().split('T')[0];
            
            const response = await fetch(`${API}/poster/projects/${projectId}`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ deadline: formattedDeadline })
            });
            
            if (response.ok) {
                const updatedProject = await response.json();
                // Update project with the response data, or fallback to formatted deadline
                setProject(prev => ({ 
                    ...prev, 
                    deadline: updatedProject.deadline || formattedDeadline 
                }));
                toast.success('Deadline updated successfully');
                setShowDeadlineModal(false);
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to update deadline');
            }
        } catch (error) {
            console.error('Error updating deadline:', error);
            toast.error('Error updating deadline');
        } finally {
            setUpdatingDeadline(false);
        }
    };
    
    const openDeadlineModal = () => {
        // Parse current deadline to set initial date
        if (project?.deadline) {
            setSelectedDeadline(new Date(project.deadline));
        } else {
            setSelectedDeadline(new Date());
        }
        setShowDeadlineModal(true);
    };

    const updateSubmissionStatus = async (submissionId, newStatus) => {
        try {
            const response = await fetch(`${API}/poster/submissions/${submissionId}/status`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });
            
            if (response.ok) {
                setSubmissions(prev => 
                    prev.map(sub => 
                        sub.id === submissionId 
                            ? { ...sub, status: newStatus }
                            : sub
                    )
                );
                toast.success(`Status updated to ${newStatus}`);
            } else {
                toast.error('Failed to update status');
            }
        } catch (error) {
            toast.error('Error updating status');
        }
    };

    const handleDragStart = (e, submission) => {
        setDraggedItem(submission);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, newStatus) => {
        e.preventDefault();
        if (draggedItem && draggedItem.status !== newStatus) {
            updateSubmissionStatus(draggedItem.id, newStatus);
        }
        setDraggedItem(null);
    };

    const playAudio = (submission) => {
        if (playingAudio === submission.id) {
            // Pause current
            if (audioElement) {
                audioElement.pause();
            }
            setPlayingAudio(null);
            return;
        }

        // Stop previous
        if (audioElement) {
            audioElement.pause();
        }

        // Play new
        const audio = new Audio(`${API}/stream/${submission.id}?token=${token}`);
        
        audio.onerror = (e) => {
            console.error('Audio playback error:', e);
            toast.error('Failed to play audio. Please try again.');
            setPlayingAudio(null);
        };
        
        audio.onended = () => setPlayingAudio(null);
        
        audio.play().catch(err => {
            console.error('Audio play error:', err);
            toast.error('Failed to start playback');
            setPlayingAudio(null);
        });
        
        setAudioElement(audio);
        setPlayingAudio(submission.id);
    };

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    // Filter submissions by search
    const filteredSubmissions = submissions.filter(sub => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            sub.track_title?.toLowerCase().includes(query) ||
            sub.user_name?.toLowerCase().includes(query) ||
            sub.user_email?.toLowerCase().includes(query)
        );
    });

    // Group submissions by status
    const groupedSubmissions = KANBAN_COLUMNS.reduce((acc, col) => {
        acc[col.id] = filteredSubmissions.filter(sub => sub.status === col.id);
        return acc;
    }, {});

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-[#F51E1E]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Back button */}
                <Link 
                    to="/poster/dashboard" 
                    className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-white transition-colors"
                >
                    <ArrowLeft size={18} />
                    <span>Back to projects</span>
                </Link>

                {/* Project Header */}
                <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-6">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <h1 className="text-2xl font-bold text-white">{project?.title}</h1>
                                <Badge className={`
                                    ${project?.status === 'open' 
                                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                        : 'bg-[#404040] text-[#888] border-[#555]'
                                    }
                                `}>
                                    {project?.status}
                                </Badge>
                            </div>
                            {/* Show poster info for admins */}
                            {isAdmin && project?.poster_name && (
                                <p className="text-sm text-[#F51E1E] mb-2">
                                    Project by: {project.poster_name}
                                    {project.poster_company && ` • ${project.poster_company}`}
                                </p>
                            )}
                            <p className="text-[#A3A3A3]">{project?.description}</p>
                        </div>
                        <div className="flex items-center gap-4">
                            {/* Invite Creator Button */}
                            {project?.status === 'open' && (
                                <Button
                                    onClick={() => setShowInviteModal(true)}
                                    className="bg-[#F51E1E] hover:bg-[#D91818] text-white"
                                    data-testid="invite-creator-btn"
                                >
                                    <Send className="w-4 h-4 mr-2" />
                                    Invite Creator
                                </Button>
                            )}
                            {/* Close/Reopen Button */}
                            {project?.status === 'open' ? (
                                <Button
                                    onClick={() => setStatusDialog({ open: true, newStatus: 'closed' })}
                                    variant="outline"
                                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                    data-testid="close-project-btn"
                                >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Close Project
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => setStatusDialog({ open: true, newStatus: 'open' })}
                                    variant="outline"
                                    className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                                    data-testid="reopen-project-btn"
                                >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Reopen Project
                                </Button>
                            )}
                            <button
                                onClick={openDeadlineModal}
                                className="flex items-center gap-2 text-[#A3A3A3] text-sm px-3 py-1.5 rounded-lg hover:bg-[#262626] hover:text-white transition-colors group"
                                data-testid="edit-deadline-btn"
                            >
                                <Calendar className="w-4 h-4" />
                                <span>Due {formatDate(project?.deadline)}</span>
                                <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                            {project?.budget && (
                                <div className="flex items-center gap-2 text-green-400 text-sm">
                                    <DollarSign className="w-4 h-4" />
                                    <span>${project.budget.toLocaleString()}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                        {project?.genres?.map((genre, idx) => (
                            <span 
                                key={idx}
                                className="text-xs px-2 py-1 bg-[#262626] rounded-full text-[#888]"
                            >
                                {genre}
                            </span>
                        ))}
                    </div>

                    {project?.reference_links?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {project.reference_links.map((link, idx) => (
                                <a
                                    key={idx}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs px-3 py-1.5 bg-[#262626] rounded-lg text-[#F51E1E] hover:bg-[#333] flex items-center gap-1"
                                >
                                    {link.text} <ExternalLink className="w-3 h-3" />
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                {/* Search Bar */}
                <div className="flex items-center justify-between">
                    <div className="relative w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                        <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by artist or track name..."
                            className="bg-[#1A1A1A] border-[#333] text-white pl-10"
                        />
                    </div>
                    <div className="text-sm text-[#A3A3A3]">
                        {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
                    </div>
                </div>

                {/* Kanban Board */}
                <div className="grid grid-cols-4 gap-4 min-h-[500px]">
                    {KANBAN_COLUMNS.map((column) => (
                        <div
                            key={column.id}
                            className="bg-[#1A1A1A] border border-[#333] rounded-xl p-4"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, column.id)}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <div className={`w-3 h-3 rounded-full ${column.color}`} />
                                    <h3 className="font-semibold text-white">{column.title}</h3>
                                </div>
                                <Badge className="bg-[#262626] text-[#888] border-[#404040]">
                                    {groupedSubmissions[column.id]?.length || 0}
                                </Badge>
                            </div>

                            <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
                                <AnimatePresence>
                                    {groupedSubmissions[column.id]?.map((submission) => (
                                        <motion.div
                                            key={submission.id}
                                            layout
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, submission)}
                                            className={`
                                                bg-[#262626] border border-[#404040] rounded-lg p-3 cursor-grab active:cursor-grabbing
                                                hover:border-[#555] transition-colors
                                                ${draggedItem?.id === submission.id ? 'opacity-50' : ''}
                                            `}
                                        >
                                            <div className="flex items-start gap-2 mb-2">
                                                <GripVertical className="w-4 h-4 text-[#555] flex-shrink-0 mt-0.5" />
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-white font-medium text-sm truncate">
                                                        {submission.track_title}
                                                    </h4>
                                                    <div className="flex items-center gap-1 text-xs text-[#888] mt-1">
                                                        <User className="w-3 h-3" />
                                                        <span className="truncate">{submission.user_name}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#333]">
                                                <div className="flex items-center gap-1 text-xs text-[#666]">
                                                    <Clock className="w-3 h-3" />
                                                    <span>{formatDate(submission.created_at)}</span>
                                                </div>
                                                <button
                                                    onClick={() => playAudio(submission)}
                                                    className={`
                                                        w-7 h-7 rounded-full flex items-center justify-center transition-colors
                                                        ${playingAudio === submission.id 
                                                            ? 'bg-[#F51E1E] text-white' 
                                                            : 'bg-[#333] text-[#888] hover:text-white hover:bg-[#404040]'
                                                        }
                                                    `}
                                                >
                                                    {playingAudio === submission.id ? (
                                                        <Pause className="w-3 h-3" />
                                                    ) : (
                                                        <Play className="w-3 h-3 ml-0.5" />
                                                    )}
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>

                                {groupedSubmissions[column.id]?.length === 0 && (
                                    <div className="text-center py-8 text-[#666] text-sm">
                                        No submissions
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Close/Reopen Confirmation Dialog */}
                <AlertDialog open={statusDialog.open} onOpenChange={(open) => !open && setStatusDialog({ open: false, newStatus: null })}>
                    <AlertDialogContent className="bg-[#1A1A1A] border-[#333]">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">
                                {statusDialog.newStatus === 'closed' ? 'Close Project' : 'Reopen Project'}
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-[#A3A3A3]">
                                {statusDialog.newStatus === 'closed' ? (
                                    <>
                                        Are you sure you want to close "<span className="text-white">{project?.title}</span>"?
                                        <br /><br />
                                        Artists will no longer be able to submit tracks to this project. 
                                        All existing submissions will be preserved and you can still manage them.
                                        You can reopen the project at any time.
                                    </>
                                ) : (
                                    <>
                                        Are you sure you want to reopen "<span className="text-white">{project?.title}</span>"?
                                        <br /><br />
                                        Artists will be able to submit tracks to this project again.
                                    </>
                                )}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="bg-[#262626] border-[#404040] text-white hover:bg-[#333]">
                                Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={toggleProjectStatus}
                                disabled={updatingStatus}
                                className={statusDialog.newStatus === 'closed' 
                                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                                    : "bg-green-600 hover:bg-green-700 text-white"
                                }
                            >
                                {updatingStatus ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    statusDialog.newStatus === 'closed' ? 'Close Project' : 'Reopen Project'
                                )}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Edit Deadline Modal */}
                <Dialog open={showDeadlineModal} onOpenChange={setShowDeadlineModal}>
                    <DialogContent className="bg-[#1A1A1A] border-[#333] max-w-sm">
                        <DialogHeader>
                            <DialogTitle className="text-white flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-[#F51E1E]" />
                                Edit Deadline
                            </DialogTitle>
                            <DialogDescription className="text-[#A3A3A3]">
                                Select a new deadline date for this project
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="flex justify-center py-4">
                            <CalendarComponent
                                mode="single"
                                selected={selectedDeadline}
                                onSelect={setSelectedDeadline}
                                disabled={(date) => date < new Date()}
                                className="rounded-md border border-[#333] bg-[#262626]"
                            />
                        </div>
                        
                        {selectedDeadline && (
                            <p className="text-center text-sm text-[#A3A3A3]">
                                New deadline: <span className="text-white font-medium">{formatDate(selectedDeadline.toISOString())}</span>
                            </p>
                        )}
                        
                        <DialogFooter className="gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowDeadlineModal(false)}
                                className="border-[#404040] text-white hover:bg-[#262626]"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleUpdateDeadline}
                                disabled={updatingDeadline || !selectedDeadline}
                                className="bg-[#F51E1E] hover:bg-[#D91818] text-white"
                            >
                                {updatingDeadline ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    'Update Deadline'
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Invite Creator Modal */}
                <InviteCreatorModal
                    open={showInviteModal}
                    onOpenChange={setShowInviteModal}
                    projectId={projectId}
                    projectTitle={project?.title}
                    onSuccess={() => toast.success('Invite sent successfully!')}
                />
            </div>
        </Layout>
    );
}
